import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useMutation} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {usePullRequestHeadSha, useUnifiedBatch, useUnifiedBatchActions} from '../stores/layout-selectors'
import {isCommittableSuggestion} from '../stores/slices/unified-batch'

type UnifiedComment = {
  id: number
  suggestionIndex?: number
}

/**
 * Commits the Commit suggestions tab of the unified batch panel.
 *
 * Mirrors {@link useApplyBatchedSuggestionsMutation} (the legacy batch path):
 * the client only sends comment identifiers — the server extracts the suggestion
 * content from each comment — so there is no client-side `SuggestedChange[]`
 * hydration. It sources comments from the `unifiedBatch` slice (excluding
 * outdated and comment-only items) instead of the legacy `suggestionBatch` slice.
 */
export function useApplyUnifiedBatchSuggestionsMutation() {
  const apiUrl = usePageDataUrl(PageData.applySuggestedChanges)
  const headOid = usePullRequestHeadSha()
  const {items} = useUnifiedBatch()
  const {clearBatch} = useUnifiedBatchActions()

  return useMutation({
    mutationFn: async ({message}: {message: string}) => {
      const comments: UnifiedComment[] = items.filter(isCommittableSuggestion).map(item => {
        const comment: UnifiedComment = {id: item.commentDatabaseId}
        if (item.suggestionIndex != null) {
          comment.suggestionIndex = item.suggestionIndex
        }
        return comment
      })

      if (comments.length === 0) {
        throw new Error('No valid suggestions found in batch')
      }

      const response = await reactFetchJSON(apiUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: {
          comments,
          message,
          expectedHeadOid: headOid,
        },
      })

      const json = await parseJSONWithBetterErrors(response)

      if (!response.ok && json?.code === 'HEAD_OID_MISMATCH') {
        throw new Error(
          'The pull request has been updated since this batch was created. Please refresh the page and try again.',
          {cause: response.status},
        )
      }

      throwErrorsIfBadResponse(response, json)
      return json
    },
    onSuccess: () => {
      clearBatch()
    },
  })
}
